import { Element, useEditor, useNode } from "@craftjs/core";
import { useSelector } from "react-redux";
import type { Recording } from "src/types";
import styled from "styled-components";

import TopLevelFloatingOptions from "@/components/editor/block-outline/floating-options/top-level-floating-component";
import Text from "@/components/editor/primitives/text/text-component";
import {
  BlockBackgroundColorWrapper,
  BlockMaxWidthWrapper,
} from "@/components/editor/shared/block-wrapper";
import {
  EmbedVideoCard,
  RecordingVideoCard,
} from "@/components/editor/shared/media/video-cards";
import { allRecordings } from "@/features/recordings/recordings-reducer";

import { VIDEO_GRID_LAYOUTS } from "../constants";
import { VideoGridSettings } from "./settings";
import {
  BlockPadding,
  EmptyBlockMessage,
  StyledFlex,
  VideoGridContainer,
} from "./styles";

export type videoProps =
  | {
      kind: "recording";
      recordingId: string;
      recording?: Recording;
      displayTitle?: string;
      displayDescription?: string;
      displayThumbnail?: string;
    }
  | {
      kind: "embed";
      url: string;
      id: string;
      thumbnail?: string;
      title?: string;
    };

export type VideoGridProps = {
  videoTitleEnabled?: boolean;
  paginationEnabled?: boolean;
  singleRowEnabled?: boolean;
  videoList?: (string | videoProps)[];
};

export const VIDEO_GRID_DISPLAY_NAME = "Video Grid";

export default function VideoGrid(props: VideoGridProps) {
  const recordings = useSelector(allRecordings);
  const videoList = props.videoList ?? [];

  const {
    id,
    connectors: { connect },
  } = useNode();

  const { isEnabled } = useEditor((state) => ({
    isEnabled: state.isEnabled(),
  }));

  if (!isEnabled && videoList.length === 0 && recordings.length === 0) {
    return null;
  }

  const getRecordingCard = (video: videoProps & { kind: "recording" }) => {
    const { recordingId, displayTitle, displayThumbnail } = video;
    const recording = recordings.find(
      (recording) => recording.id === recordingId
    );
    if (recording) {
      return (
        <Container key={recordingId}>
          <RecordingVideoCard
            showCaption={props.videoTitleEnabled}
            recording={recording}
            displayTitle={displayTitle ?? null}
            displayThumbnailUrl={displayThumbnail ?? null}
          />
        </Container>
      );
    }
    return null;
  };

  const getEmbedCard = (video: videoProps & { kind: "embed" }) => {
    const { id, url, thumbnail, title } = video;
    return (
      <Container key={id}>
        <EmbedVideoCard
          url={url}
          thumbnail={thumbnail}
          title={title}
          aspectRatio="square"
          showCaption={props.videoTitleEnabled}
        />
      </Container>
    );
  };

  const videoCards = videoList.reduce((_videoCards, currentVideo) => {
    if (typeof currentVideo === "string") return _videoCards;

    const videoCard =
      currentVideo.kind === "recording"
        ? getRecordingCard(currentVideo)
        : getEmbedCard(currentVideo);

    return videoCard ? [..._videoCards, videoCard] : _videoCards;
  }, []);

  return (
    <BlockBackgroundColorWrapper ref={connect} id={id}>
      <BlockMaxWidthWrapper>
        <BlockPadding>
          <StyledFlex>
            <Element
              id="video-grid-title"
              is={Text}
              element="h2"
              text="Video Grid"
              pattern="headingTwo"
            />
          </StyledFlex>

          {isEnabled && videoCards.length === 0 ? (
            <EmptyBlockMessage>
              No videos selected. Please choose videos from the block&apos;s
              settings
            </EmptyBlockMessage>
          ) : (
            <VideoGridContainer>{videoCards}</VideoGridContainer>
          )}
        </BlockPadding>
      </BlockMaxWidthWrapper>
    </BlockBackgroundColorWrapper>
  );
}

VideoGrid.craft = {
  props: {
    layout: VIDEO_GRID_LAYOUTS.STANDARD,
    videoList: [],
    videoTitleEnabled: true,
  },
  custom: {
    displayName: VIDEO_GRID_DISPLAY_NAME,
  },
  related: {
    settings: VideoGridSettings,
    leftOutlineOptions: TopLevelFloatingOptions,
  },
  rules: {
    canMoveIn: () => false,
  },
};

const Container = styled.div`
  overflow: hidden;
  position: relative;
  max-width: 282px;
  max-height: 282px;
`;
